import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Download, Mail, Phone, Calendar, Package, Clock, Users, Info } from 'lucide-react';
import { format } from 'date-fns';
import ApiService from '../services/api';

const ThankYouPage = () => {
  const navigate = useNavigate();
  const [complaintData, setComplaintData] = useState(null);
  const [productName, setProductName] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      const data = localStorage.getItem('complaintData');
      if (data) {
        const parsedData = JSON.parse(data);
        setComplaintData(parsedData);
        
        // Fetch product name
        try {
          const name = await ApiService.getProductName(parsedData.product_id);
          setProductName(name);
        } catch (error) {
          console.error('Error fetching product name:', error);
          setProductName(`Product ${parsedData.product_id}`);
        }
      } else {
        navigate('/');
      }
      setLoading(false);
    };
    
    fetchData();
  }, [navigate]);

  const handleDownloadReceipt = () => {
    if (!complaintData) return;
    
    const receipt = `
COMPLAINT RECEIPT
================

Complaint ID: ${complaintData.complaint_id}
Submission Date: ${format(new Date(complaintData.submission_date), 'PPP')}
Customer ID: ${complaintData.customer_id}
Contact Email: ${complaintData.customer_contact_details}

Product Information:
- Product ID: ${complaintData.product_id}
- Batch ID: ${complaintData.batch_id}
- Production Date: ${complaintData.production_date || 'Not specified'}

Complaint Details:
- Severity: ${complaintData.severity}
- Business Impact: ${complaintData.business_impact}
- Description: ${complaintData.defect_description}

Additional Information:
- Cost Estimate: $${complaintData.cost_estimate || '0.00'}
- Status: ${complaintData.status || 'Reported'}
- Is Repeat: ${complaintData.is_repeat ? 'Yes' : 'No'}

Thank you for submitting your complaint. We will review it and get back to you soon.
    `;

    const blob = new Blob([receipt], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `complaint-${complaintData.complaint_id}-receipt.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading || !complaintData) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const Section = ({ title, icon: Icon, children, className = '' }) => (
    <div className={`mb-8 ${className}`}>
      <div className="flex items-center space-x-3 mb-6">
        <div className="p-2.5 bg-primary-600 rounded-xl">
          <Icon className="h-5 w-5 text-white" />
        </div>
        <h2 className="text-xl font-bold text-gray-900">{title}</h2>
      </div>
      {children}
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto px-4">
      {/* Main Content Card */}
      <motion.div 
        className="card p-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        {/* Complaint Summary */}
        <Section title="Complaint Summary" icon={Info}>
          <div className="flex items-center space-x-2 text-sm text-gray-500 mb-6">
            <Calendar className="h-4 w-4" />
            <span>Submitted on {format(new Date(complaintData.submission_date), 'MMMM do, yyyy')}</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Complaint ID Card */}
            <div className="bg-blue-50 p-6 rounded-lg border border-blue-100">
              <div className="flex items-center space-x-2 text-blue-600 mb-2">
                <Info className="h-4 w-4" />
                <span className="text-sm font-medium">Complaint ID</span>
              </div>
              <p className="text-3xl font-bold text-blue-600 mb-1">#{complaintData.complaint_id}</p>
              <p className="text-xs text-blue-600">Keep this ID for future reference</p>
            </div>

            {/* Contact Info */}
            <div className="space-y-3">
              <div className="flex items-center space-x-2 text-sm">
                <Mail className="h-4 w-4 text-gray-400" />
                <span className="text-gray-500">Contact Email:</span>
                <span className="font-medium text-gray-900">{complaintData.customer_contact_details}</span>
              </div>
              <div className="flex items-center space-x-2 text-sm">
                <Package className="h-4 w-4 text-gray-400" />
                <span className="text-gray-500">Product:</span>
                <span className="font-medium text-gray-900">{productName}</span>
              </div>
            </div>
          </div>

          {/* Complaint Details */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Complaint Details</h3>
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <p className="text-gray-700 text-sm leading-relaxed">{complaintData.defect_description}</p>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="text-center p-4 bg-white rounded-lg border border-gray-200">
              <span className="text-xs text-gray-400 uppercase tracking-wide block mb-1">Severity</span>
              <p className="font-semibold capitalize text-gray-900">{complaintData.severity}</p>
            </div>
            <div className="text-center p-4 bg-white rounded-lg border border-gray-200">
              <span className="text-xs text-gray-400 uppercase tracking-wide block mb-1">Business Impact</span>
              <p className="font-semibold capitalize text-gray-900 text-sm">{complaintData.business_impact?.replace('_', ' ')}</p>
            </div>
            <div className="text-center p-4 bg-white rounded-lg border border-gray-200">
              <span className="text-xs text-gray-400 uppercase tracking-wide block mb-1">Status</span>
              <p className="font-semibold capitalize text-gray-900">{complaintData.status || 'Reported'}</p>
            </div>
            <div className="text-center p-4 bg-white rounded-lg border border-gray-200">
              <span className="text-xs text-gray-400 uppercase tracking-wide block mb-1">Cost Estimate</span>
              <p className="font-semibold text-gray-900">${complaintData.cost_estimate || '0.00'}</p>
            </div>
          </div>
        </Section>

        {/* Next Steps */}
        <Section title="What Happens Next?" icon={Clock}>
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-7 h-7 bg-primary-600 rounded-full flex items-center justify-center">
                <span className="text-xs font-bold text-white">1</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Acknowledgment</h3>
                <p className="text-sm text-gray-600 mt-1">You'll receive an email confirmation within 24 hours with your complaint details.</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-7 h-7 bg-primary-600 rounded-full flex items-center justify-center">
                <span className="text-xs font-bold text-white">2</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Investigation</h3>
                <p className="text-sm text-gray-600 mt-1">Our quality team will investigate your complaint within 3-5 business days.</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-7 h-7 bg-primary-600 rounded-full flex items-center justify-center">
                <span className="text-xs font-bold text-white">3</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Resolution</h3>
                <p className="text-sm text-gray-600 mt-1">We'll contact you with our findings and proposed resolution plan.</p>
              </div>
            </div>
          </div>
        </Section>

        {/* Contact Information */}
        <Section title="Need Help?" icon={Users}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="flex items-center space-x-3 p-5 bg-blue-50 rounded-lg border border-blue-100">
              <Phone className="h-6 w-6 text-blue-600" />
              <div>
                <p className="font-semibold text-gray-900">Customer Support</p>
                <p className="text-sm text-blue-600 font-medium">1-800-SUPPORT</p>
              </div>
            </div>
            <div className="flex items-center space-x-3 p-5 bg-purple-50 rounded-lg border border-purple-100">
              <Mail className="h-6 w-6 text-purple-600" />
              <div>
                <p className="font-semibold text-gray-900">Email Support</p>
                <p className="text-sm text-purple-600 font-medium">support@company.com</p>
              </div>
            </div>
          </div>
        </Section>

        {/* Action Buttons */}
        <div className="pt-6 mt-6 border-t border-gray-200">
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={handleDownloadReceipt}
              className="btn-secondary flex items-center justify-center space-x-2 text-sm"
            >
              <Download className="h-4 w-4" />
              <span>Download Receipt</span>
            </button>
            <button
              onClick={() => navigate('/')}
              className="btn-primary flex items-center justify-center space-x-2 text-sm"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Submit Another Complaint</span>
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ThankYouPage;